<template>

    <ui-view v-spinner="loading" :hide="loading">

        <ui-heading :title="pageTitle" icon="magicon-user">
            <ui-button
                v-if="itemId"
                color="negative"
                icon="magicon-trash"
                @click="deleteUser">Verwijderen</ui-button>
            <ui-button color="positive" :loading="submitting" @click="$refs.form.submit()">Opslaan</ui-button>

            <ui-button
                color="medium"
                type="bordered"
                @click="$router.go(-1)"
                slot="left">Annuleren</ui-button>
        </ui-heading>

        <ui-form-panel auto-validate ref="form" @submit="submit">

            <ui-form-input
                title="E-mail adres"
                name="email"
                rules="required|email"
                v-model="pendingUser.email"/>

            <ui-form-input
                title="Gebruikersnaam"
                name="username"
                rules="required"
                v-model="pendingUser.username"/>

            <ui-form-input
                title="Telefoonnummer"
                name="phoneNumber"
                v-model="pendingUser.phoneNumber"/>

            <ui-form-input
                v-if="!itemId"
                title="Wachtwoord"
                type="password"
                name="password"
                rules="required"
                v-model="pendingUser.password"/>

            <ui-form-input
                v-if="itemId"
                title="Nieuw wachtwoord"
                type="password"
                name="password"
                v-model="pendingUser.password"/>

            <ui-form-input
                v-if="(pendingUser.password && pendingUser.password.length) || !itemId"
                title="Wachtwoord herhalen"
                type="password"
                name="passwordConfirm"
                rules="required|confirmed:password"
                v-model.trim="passwordConfirm"/>

            <ui-form-radio
                v-if="isAdmin"
                title="Rol"
                name="role"
                :options="roleOptions"
                rules="required"
                v-model="pendingUser.role"/>

            <ui-form-dropdown
                v-if="isAdmin && pendingUser.role !== 'ADMIN'"
                title="Mosselkweker"
                :options="growerOptions"
                v-model="pendingUser.growerId"/>

            <ui-form-dropdown
                v-if="pendingUser.role !== 'ADMIN'"
                empty
                title="Schip"
                :options="shipOptions"
                :disabled="!pendingUser.growerId"
                v-model="pendingUser.shipId"/>

        </ui-form-panel>

    </ui-view>

</template>

<script>

import Vue from 'vue';
import { clone, pick } from 'es-toolkit';
import Component from 'vue-class-component';

import getGraphQLError from 'js-core/js/utils/getGraphQLError';
import createQuery from 'js-core/js/apollo/queries/createQuery';

import UserRole from 'common/enums/UserRole';
import ApiErrorCode from 'common/enums/ApiErrorCode';

import FindUserQuery from 'gql/queries/UserForm/FindUser.gql';
import AllShipsQuery from 'gql/queries/UserForm/AllShips.gql';
import AllGrowersQuery from 'gql/queries/UserForm/AllGrowers.gql';

import UserMutations from 'app/mutations/UserMutations';

@Component({

    name: 'UserForm',
    apollo: {

        user: createQuery(FindUserQuery, {

            skip(){

                return !this.itemId;
            },

            response(response){

                this.pendingUser = {
                    ...pick(response, Object.keys(this.pendingUser)),
                    password: null
                };

            },

            variables(){

                return {
                    userId: this.itemId
                };
            }
        }),
        growers: createQuery(AllGrowersQuery, {

            skip() {

                return !this.isAdmin;
            }

        }),
        ships: createQuery(AllShipsQuery, {

            skip() {

                return !this.pendingUser.growerId;
            },

            variables() {

                return {
                    filter: {
                        growerId: [this.pendingUser.growerId]
                    }
                };
            }
        })
    }
})
export default class UserForm extends Vue
{
    pendingUser = {
        email: null,
        phoneNumber: null,
        password: null,
        username: null,
        role: UserRole.GROWER,
        growerId: !this.isAdmin ? this.$user.growerId : null,
        shipId: null
    };

    passwordConfirm = null;

    loading = 0;
    submitting = false;

    breadcrumb(){

        return [{
            title: 'Gebruikers',
            route: {
                name: 'users'
            }
        }, this.pageTitle];
    }

    created(){

        this.$watch('pendingUser.role', () => {

            if(this.pendingUser.role === UserRole.ADMIN) {

                this.pendingUser.growerId = null;
                this.pendingUser.shipId = null;
            }
        });
    }

    async submit(){

        this.submitting = true;

        const input = clone({
            ...this.pendingUser
        });

        try {

            if (this.user) {

                input.id = this.user.id;

                if (!input.password) {
                    delete input.password;
                }

                await UserMutations.update(input);

                this.logger.info('User updated');
                this.$toast.success('De gebruiker is opgeslagen');
            }
            else {

                await UserMutations.create(input);

                this.logger.info('User created');
                this.$toast.success('De gebruiker is aangemaakt');
            }

            this.$router.push({
                name: 'users'
            });
        }
        catch(e){

            let error = getGraphQLError(e);

            this.logger.error('Error updating/creating user', e);

            if(error && error.code === ApiErrorCode.USER_EMAIL_EXISTS){
                this.$toast.error('De gekozen gebruikersnaam is al in gebruik');
            }
            else {
                this.$toast.error('De gebruiker kon niet worden opgeslagen');
            }
        }

        this.submitting = false;
    }

    async deleteUser(){

        const { result, overlay } = await this.$alert.delete('Weet je zeker dat je deze gebruiker wilt verwijderen?');
        if(!result){
            return;
        }

        try {

            await UserMutations.delete(this.user.id);

            this.logger.info(`User ${this.user.id} deleted`);
            this.$toast.success('De gebruiker is verwijderd');

            this.$router.replace({
                name: 'users'
            });
        }
        catch(e){

            this.logger.error(`Error deleting user ${this.user.id}`, e);
            this.$toast.error('De gebruiker kon niet worden verwijderd');
        }

        this.$overlay.close(overlay);
    }

    get pageTitle(){

        return this.itemId ? this.$safe('user.email') : 'Gebruiker toevoegen';
    }

    get itemId(){

        return this.$route.params.id;
    }

    get roleOptions(){

        return UserRole.options();
    }

    get growerOptions() {

        if(!this.growers) {
            return [];
        }

        return this.growers.items.map(grower => ({
            label: grower.name,
            value: grower.id
        }));
    }

    get shipOptions() {

        if(!this.ships) {
            return [];
        }

        return this.ships.items.map(ship => ({
            label: ship.name,
            value: ship.id
        }));
    }
}

</script>